import { Fragment, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TileAssembly from '../components/TileAssembly';
import Reveal from '../components/Reveal';
import ParallaxImage from '../components/ParallaxImage';
import { HowDefs, StepChoose, StepMake, StepTogether } from '../components/HowDrawings';
import '../App.css';
import './Home.css';

const STEPS = [
  ['create', StepChoose],
  ['make', StepMake],
  ['hang', StepTogether],
];
const OCCASION_KEYS = ['birthdays', 'weddings', 'retirements', 'teamFarewells', 'anniversaries'];
// Three copies side by side, scrolled by exactly one copy's width, so the
// strip loops without a visible seam on any screen width.
const MARQUEE_COPIES = 3;

function Home() {
  const { t } = useTranslation();
  const { hash } = useLocation();

  // The hero is a scroll-driven animation, so it has to start from its first
  // frame: coming back to the homepage (by link, back button or reload) must
  // not resume wherever the browser last was. A link to a section
  // (/#how-it-works) is the one exception. The browser's own scroll memory is
  // switched off while this page is open, and the smooth scrolling the page
  // uses elsewhere is skipped for this jump.
  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, [hash]);

  // The page is white at both ends and paper in between, and the whole page
  // (header and footer included) fades from one to the other - the colours
  // are in Home.css, under html.home-white-top / html.home-white-bottom.
  // White while the opening animation is still in the bottom quarter of the
  // screen, and from the moment "Perfect for" passes the middle of it. It
  // starts white, so the page doesn't fade in.
  const openingRef = useRef(null);
  const occasionsRef = useRef(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('home-white-top');
    const top = new IntersectionObserver(
      ([entry]) => root.classList.toggle('home-white-top', entry.isIntersecting),
      { rootMargin: '-75% 0px 0px 0px' }
    );
    const bottom = new IntersectionObserver(
      ([entry]) => {
        const stillBelow = !entry.isIntersecting && entry.boundingClientRect.top > 0;
        root.classList.toggle('home-white-bottom', !stillBelow);
      },
      { rootMargin: '0px 0px -50% 0px' }
    );
    top.observe(openingRef.current);
    bottom.observe(occasionsRef.current);
    return () => {
      top.disconnect();
      bottom.disconnect();
      root.classList.remove('home-white-top', 'home-white-bottom');
    };
  }, []);

  return (
    <div className="home">
      <div ref={openingRef}>
        <TileAssembly />
      </div>

      <section className="h-intro h-container">
        <Reveal as="h2" className="h-display h-intro-word">
          {t('home.intro.word')}
        </Reveal>
        <div className="h-intro-body">
          <Reveal as="p" delay={120}>
            {t('home.intro.body')}
          </Reveal>
          <Reveal delay={240}>
            <Link to="/order" className="h-link">
              {t('home.cta')} <span aria-hidden="true">&rarr;</span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="h-photo h-container">
        <ParallaxImage
          className="h-photo-wide"
          src="/media/paiting_tog.jpg"
          alt={t('home.photos.night.alt')}
          caption={t('home.photos.night.caption')}
          ratio="1280 / 853"
        />
      </section>

      <section id="how-it-works" className="h-how h-container">
        <HowDefs />
        <div className="h-how-text">
          <Reveal as="h2" className="h-display">
            {t('home.howItWorks.title')}
          </Reveal>
          <Reveal as="p" className="h-label h-how-lead" delay={100}>
            {t('home.howItWorks.lead')}
          </Reveal>

          <ol className="h-steps">
            {STEPS.map(([key, Drawing], i) => (
              <Reveal as="li" className="h-step" key={key} delay={i * 130}>
                <span className="h-step-num" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3>{t(`home.howItWorks.steps.${key}.title`)}</h3>
                  <p>{t(`home.howItWorks.steps.${key}.description`)}</p>
                </div>
                <div className="h-step-art">
                  <Drawing />
                </div>
              </Reveal>
            ))}
          </ol>

          <Link to="/how-it-works" className="h-link h-how-more">
            {t('home.howItWorks.more')} <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

        <ParallaxImage
          className="h-how-photo"
          src="/media/painting-table.jpg"
          alt={t('home.photos.table.alt')}
          caption={t('home.photos.table.caption')}
          ratio="1200 / 1488"
          speed={0.14}
        />
      </section>

      <section className="h-gallery">
        <Reveal as="h2" className="h-display h-center">
          {t('home.gallery.title')}
        </Reveal>
        <div className="h-marquee">
          <div className="h-marquee-track">
            {Array.from({ length: MARQUEE_COPIES }).map((_, i) => (
              <img
                key={i}
                src="/media/painting-row.jpg"
                alt={i === 0 ? t('home.gallery.alt') : ''}
                aria-hidden={i === 0 ? undefined : 'true'}
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        </div>
        <p className="h-caption h-center">{t('home.gallery.caption')}</p>
      </section>

      <section className="h-occasions h-container" ref={occasionsRef}>
        <Reveal as="p" className="h-label">
          {t('home.occasions.label')}
        </Reveal>
        <p className="h-occasion-list">
          {OCCASION_KEYS.map((key, i) => (
            <Fragment key={key}>
              <Reveal as="span" className="h-occasion-wrap" delay={i * 90}>
                <span className="h-occasion">{t(`home.occasions.items.${key}`)}</span>
                {i < OCCASION_KEYS.length - 1 && (
                  <span className="h-occasion-sep" aria-hidden="true">
                    {' / '}
                  </span>
                )}
              </Reveal>{' '}
            </Fragment>
          ))}
        </p>
      </section>

      <section className="h-closing h-container">
        <Reveal as="p" className="h-label">
          {t('home.closing.label')}
        </Reveal>
        <Reveal delay={140}>
          <Link to="/order" className="h-closing-link">
            {t('home.cta')}
            <span className="h-arrow" aria-hidden="true">
              &rarr;
            </span>
          </Link>
        </Reveal>
      </section>
    </div>
  );
}

export default Home;
